import { useEffect, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { USAGE_LIMIT_BANNER } from 'ui-addon-upgrades/_core/common/constants/PurchaseMotionLocations';
import { PRICING_PAGE_REDIRECT } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import UIPurchaseMotionButton from 'ui-addon-upgrades/button/UIPurchaseMotionButton';
import { getProductNameTextFromApiName } from '../../_core/common/adapters/getProductNameText';
import { track } from '../../_core/common/eventTracking/tracker';
import getUpgradeBannerTranslations from '../../_core/utils/getUpgradeBannerTranslations';
import { getUpgradeDataFromUpgradeConfig } from '../../_core/utils/getUpgradeDataFromUpgradeConfig';
import displayAddOnLimitIncreaseCopy from '../displayAddOnLimitIncreaseCopy';
import { getBannerI18nSubTitleKey, getBannerI18nTitleKey, getUpgradeConfigTrackingProperties, updateViewedUpgradePointsCache } from '../utils';
import DefaultBanner from './DefaultBanner';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const UsageLimitBannerSubtext = ({
  apiName,
  featureTranslationKey,
  subtitle
}) => {
  const translatedProductName = getProductNameTextFromApiName(apiName);
  if (subtitle) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: `${subtitle} ${translatedProductName}.`
    });
  }
  const addOnCopyOverride = displayAddOnLimitIncreaseCopy(apiName);
  if (addOnCopyOverride) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: getBannerI18nSubTitleKey(`upgrades.usageLimitBanner.subtext`, featureTranslationKey)
      }), "\xA0", /*#__PURE__*/_jsx(FormattedMessage, {
        message: `upgrades.usageLimitBanner.addOnLimitIncreaseOverride`,
        options: {
          productName: translatedProductName
        }
      })]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: getBannerI18nSubTitleKey(`upgrades.usageLimitBanner.subtext`, featureTranslationKey)
    }), "\xA0", translatedProductName && /*#__PURE__*/_jsx(FormattedMessage, {
      message: translatedProductName ? `upgrades.message.productName` : '',
      options: {
        productName: translatedProductName
      }
    })]
  });
};
const UsageLimitBanner = ({
  upgradePoint,
  limit,
  value,
  displayThreshold,
  hasReachedLimit,
  marginStyle,
  'data-test-id': dataTestId = 'usage-limit-banner',
  displayBannerOverride = false
}) => {
  const upgradeData = getUpgradeDataFromUpgradeConfig(upgradePoint);
  const usageLimitBannerData = useMemo(() => ({
    limit,
    value,
    displayThreshold,
    hasReachedLimit
  }), [limit, value, displayThreshold, hasReachedLimit]);
  useEffect(() => {
    updateViewedUpgradePointsCache(upgradePoint.upgradeKey, () => {
      track('interaction', Object.assign({
        action: 'viewed upgrade point'
      }, usageLimitBannerData, getUpgradeConfigTrackingProperties(upgradePoint)));
    });
  }, [upgradePoint, usageLimitBannerData]);
  if (!displayBannerOverride && displayThreshold && value < displayThreshold) return null;
  const {
    bannerTitle,
    bannerSubtitle
  } = getUpgradeBannerTranslations(upgradePoint.descriptions);
  return /*#__PURE__*/_jsx(DefaultBanner, {
    titleText: bannerTitle ? /*#__PURE__*/_jsx(_Fragment, {
      children: bannerTitle
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: getBannerI18nTitleKey('upgrades.usageLimitBanner.header', upgradePoint.featureTranslationKey),
      options: {
        value,
        limit,
        count: value
      }
    }),
    marginStyle: marginStyle,
    dataTestId: dataTestId,
    subtext: /*#__PURE__*/_jsx(UsageLimitBannerSubtext, {
      apiName: upgradeData.apiName,
      featureTranslationKey: upgradePoint.featureTranslationKey,
      subtitle: bannerSubtitle
    }),
    button: /*#__PURE__*/_jsx(UIPurchaseMotionButton, {
      upgradeData: upgradeData,
      usageLimitBannerData: usageLimitBannerData,
      location: USAGE_LIMIT_BANNER,
      level: 1,
      use: "link",
      purchaseMotions: [PRICING_PAGE_REDIRECT],
      "data-test-id": "upgrade-button",
      isUpgradePoint: true,
      goToUpgradePage: true,
      truncate: false,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.upgradeButton.default"
      })
    })
  });
};
export default UsageLimitBanner;